<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import type { ApiClient } from '@xjlv2/api-client'
import { RrButton, RrInput, RrModal, RrSelect, RrUpload, type UploadFile, type UploadRequestOptions } from '@xjlv2/ui'
import { parseUploadResponse, validateUploadPreflight } from './materialCenterUtils'

const props = withDefaults(defineProps<{
  open: boolean
  scopeOptions: Array<{ code: string; name: string }>
  initialScope?: string
  defaultCategory?: string
  defaultProductCode?: string
  defaultAppCode?: string
  createApiClient: () => ApiClient
}>(), {
  initialScope: '',
  defaultCategory: '',
  defaultProductCode: '',
  defaultAppCode: ''
})

const emit = defineEmits<{
  'update:open': [val: boolean]
  notice: [message: string]
  error: [message: string]
  uploaded: []
}>()

const uploadScope = ref('')
const uploadCategory = ref('')
const uploadProductCode = ref('')
const uploadAppCode = ref('')
const uploadFileList = ref<UploadFile[]>([])

const moduleScopeOptions = computed(() =>
  props.scopeOptions.map((o) => ({ label: o.name, value: o.code }))
)

const isUploading = computed(() => uploadFileList.value.some((f) => f.status === 'uploading'))

let uploadFinishDebounce: number | undefined
function scheduleUploadedEmit() {
  if (uploadFinishDebounce != null) clearTimeout(uploadFinishDebounce)
  uploadFinishDebounce = window.setTimeout(() => {
    emit('uploaded')
  }, 300)
}

watch(() => props.open, (isOpen) => {
  if (isOpen) {
    const validScope = props.initialScope && props.scopeOptions.some((o) => o.code === props.initialScope)
      ? props.initialScope
      : (props.scopeOptions[0]?.code ?? '')
    uploadScope.value = validScope
    uploadCategory.value = props.defaultCategory || ''
    uploadProductCode.value = props.defaultProductCode || ''
    uploadAppCode.value = props.defaultAppCode || ''
    uploadFileList.value = []
  }
})

function beforeCustomUpload(file: File) {
  const result = validateUploadPreflight(uploadScope.value, file.name, uploadCategory.value)
  if (!result.valid) {
    emit('error', result.error || '上传前置校验失败')
    return false
  }
  return true
}

function handleCustomUpload(options: UploadRequestOptions) {
  const xhr = new XMLHttpRequest()
  const formData = new FormData()
  const fileObj = options.file instanceof File ? options.file : new File([options.file], 'upload')
  formData.append('file', fileObj)

  const params = new URLSearchParams()
  params.set('moduleCode', uploadScope.value)
  if (uploadCategory.value.trim()) {
    params.set('category', uploadCategory.value.trim())
  }
  if (uploadProductCode.value.trim()) {
    params.set('productCode', uploadProductCode.value.trim())
  }
  if (uploadAppCode.value.trim()) {
    params.set('appCode', uploadAppCode.value.trim())
  }

  const api = props.createApiClient()
  const gateway = api.gateway?.replace(/\/+$/, '') || ''
  const url = `${gateway}/api/saas/storage/upload?${params.toString()}`

  xhr.open('POST', url, true)
  if (api.token) {
    xhr.setRequestHeader('Authorization', `Bearer ${api.token}`)
  }

  xhr.upload.onprogress = (e) => {
    if (e.lengthComputable) {
      const percent = Math.round((e.loaded / e.total) * 100)
      options.onProgress({ percent })
    }
  }

  xhr.onload = () => {
    if (xhr.status >= 200 && xhr.status < 300) {
      try {
        const json = JSON.parse(xhr.responseText)
        const parsed = parseUploadResponse(json)
        if (parsed.success && parsed.data) {
          options.onSuccess(parsed.data)
          emit('notice', `素材“${parsed.data.fileName || fileObj.name}”已成功上传并入库（ID: ${parsed.data.materialId}）`)
          scheduleUploadedEmit()
        } else {
          const errMsg = parsed.error || '上传失败'
          const err = new Error(errMsg)
          options.onError(err, json)
          emit('error', errMsg)
        }
      } catch {
        const parseErr = new Error('服务端响应解析失败')
        options.onError(parseErr)
        emit('error', parseErr.message)
      }
    } else {
      let errMsg = `上传失败 (${xhr.status})`
      try {
        const json = JSON.parse(xhr.responseText)
        if (typeof json.message === 'string' && json.message) errMsg = json.message
      } catch {
        // 忽略非 JSON 响应解析
      }
      const err = new Error(errMsg)
      options.onError(err)
      emit('error', errMsg)
    }
  }

  xhr.onerror = () => {
    const err = new Error('网络异常，上传请求失败')
    options.onError(err)
    emit('error', err.message)
  }

  xhr.onabort = () => {
    const err = new Error('上传已取消')
    options.onError(err)
  }

  xhr.send(formData)

  return {
    abort: () => {
      xhr.abort()
    }
  }
}

function handleClose() {
  if (!isUploading.value) {
    emit('update:open', false)
  }
}
</script>

<template>
  <RrModal
    :open="open"
    title="上传素材"
    description="文件上传至对象存储后将自动登记入库，无需重复操作"
    :width="640"
    :closable="!isUploading"
    :mask-closable="!isUploading"
    @update:open="(val) => { if (!isUploading) emit('update:open', val) }"
  >
    <div class="upload-modal-body">
      <div class="upload-form-grid">
        <label>
          <span class="label-text">业务模块 <em class="required-star">*</em></span>
          <RrSelect
            v-model:value="uploadScope"
            :options="moduleScopeOptions"
            :disabled="isUploading"
            placeholder="请选择业务模块"
          />
        </label>
        <label>
          <span class="label-text">分类</span>
          <RrInput
            v-model:value="uploadCategory"
            :disabled="isUploading"
            placeholder="例如：首页轮播"
            :maxlength="100"
          />
        </label>
        <label>
          <span class="label-text">产品编码</span>
          <RrInput
            v-model:value="uploadProductCode"
            :disabled="isUploading"
            placeholder="可选，关联特定产品"
            :maxlength="64"
          />
        </label>
        <label>
          <span class="label-text">应用编码</span>
          <RrInput
            v-model:value="uploadAppCode"
            :disabled="isUploading"
            placeholder="可选，关联特定应用"
            :maxlength="64"
          />
        </label>
      </div>

      <div class="upload-area">
        <RrUpload
          drag
          multiple
          :file-list="uploadFileList"
          :custom-request="handleCustomUpload"
          :before-upload="beforeCustomUpload"
          accept="image/*,video/*,audio/*,application/pdf,text/*"
          @update:file-list="(files) => uploadFileList = files"
        >
          <template #dragHint>
            <span class="rr-upload__drag-hint">支持图片、音视频、文档等素材，自动识别格式并入库</span>
          </template>
        </RrUpload>
      </div>
    </div>

    <template #footer>
      <RrButton :disabled="isUploading" @click="handleClose">
        {{ isUploading ? '上传中...' : '完成 / 关闭' }}
      </RrButton>
    </template>
  </RrModal>
</template>

<style scoped>
.upload-modal-body {
  padding: 4px 0 0;
}

.upload-form-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 14px;
  margin-bottom: 18px;
}

.upload-form-grid label {
  display: grid;
  gap: 6px;
  color: var(--rr-color-text);
  font-size: 13px;
  font-weight: 500;
}

.label-text {
  display: flex;
  align-items: center;
  gap: 4px;
}

.required-star {
  color: var(--rr-color-danger);
  font-style: normal;
}

.upload-area {
  border: 1px dashed var(--rr-color-border-strong);
  border-radius: 8px;
  padding: 16px;
  background: var(--rr-color-surface-subtle);
}

@media (max-width: 600px) {
  .upload-form-grid {
    grid-template-columns: 1fr;
  }
}
</style>
